// =============================================================================
// VulnTracker — Projects Page
// =============================================================================
import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  FolderIcon,
  PlusIcon,
  XMarkIcon,
  InformationCircleIcon,
  ClipboardDocumentIcon,
  PencilSquareIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { useProjects, useCreateProject, useUpdateProject, useDeleteProject, useOrganizations } from '@/api/hooks';
import type { Project } from '@/types';

// ── Empty State ──────────────────────────────────────────────────────────
function EmptyState({ onAddProject }: { onAddProject: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 px-4">
      <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-primary-50">
        <FolderIcon className="h-10 w-10 text-primary-500" />
      </div>
      <h3 className="mt-6 text-xl font-semibold text-gray-900">
        No Projects Yet
      </h3>
      <p className="mt-2 max-w-md text-center text-sm text-gray-500">
        Get started by creating your first project. Projects help you organize
        and track software components, enabling automatic vulnerability
        monitoring and security notifications for your team.
      </p>
      <button
        type="button"
        onClick={onAddProject}
        className="btn-primary mt-8 gap-2"
      >
        <PlusIcon className="h-5 w-5" />
        Create Your First Project
      </button>
    </div>
  );
}

// ── Tooltip ──────────────────────────────────────────────────────────────
function InfoTooltip({ text }: { text: string }) {
  const [show, setShow] = useState(false);

  return (
    <span className="relative inline-flex items-center ml-1.5">
      <InformationCircleIcon
        className="h-4 w-4 text-gray-400 hover:text-primary-600 cursor-help transition-colors"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      />
      {show && (
        <span className="absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2 whitespace-normal rounded-lg bg-gray-900 px-3 py-2 text-xs font-normal text-white shadow-lg w-64 text-center leading-relaxed">
          {text}
          <span className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </span>
      )}
    </span>
  );
}

// ── Add Project Modal ────────────────────────────────────────────────────
function AddProjectModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [projectManager, setProjectManager] = useState('');
  const [securityExpert, setSecurityExpert] = useState('');
  const [securityExpertEmail, setSecurityExpertEmail] = useState('');
  const [description, setDescription] = useState('');
  const createProject = useCreateProject();

  // Fetch organizations for dropdown
  const { data: orgsData, isLoading: orgsLoading } = useOrganizations({ page_size: 100 });
  const organizations = orgsData?.items ?? [];

  // Generate a preview UUID
  const [previewUUID] = useState(() => crypto.randomUUID());

  const PERSONAL_DOMAINS = new Set(['gmail.com','yahoo.com','hotmail.com','outlook.com','live.com','aol.com','icloud.com','me.com','mac.com','protonmail.com','proton.me','tutanota.com','gmx.com','gmx.net','mail.com','yandex.com','yandex.ru','zoho.com','qq.com','163.com','126.com','msn.com','rediffmail.com','inbox.com','fastmail.com']);
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const emailDomain = securityExpertEmail.split('@')[1]?.toLowerCase() ?? '';
  const isEmailValid = EMAIL_RE.test(securityExpertEmail) && !PERSONAL_DOMAINS.has(emailDomain);
  const isValid = name.trim().length > 0 && organizationId.trim().length > 0 && projectManager.trim().length > 0 && securityExpert.trim().length > 0 && isEmailValid;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    try {
      await createProject.mutateAsync({
        name: name.trim(),
        organization_id: organizationId,
        project_manager: projectManager.trim(),
        security_expert: securityExpert.trim(),
        security_expert_email: securityExpertEmail.trim().toLowerCase(),
        description: description.trim(),
      });
      toast.success('Project created successfully!');
      setName('');
      setOrganizationId('');
      setProjectManager('');
      setSecurityExpert('');
      setSecurityExpertEmail('');
      setDescription('');
      onClose();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { data?: { detail?: string } } };
        toast.error(axiosErr.response?.data?.detail || 'Failed to create project');
      } else {
        toast.error('Failed to create project. Please try again.');
      }
    }
  };

  const handleCopyUUID = () => {
    navigator.clipboard.writeText(previewUUID);
    toast.success('UUID copied to clipboard');
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-lg transform rounded-xl bg-white shadow-2xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50">
                      <FolderIcon className="h-5 w-5 text-primary-600" />
                    </div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Create New Project
                    </Dialog.Title>
                  </div>
                  <button
                    type="button"
                    className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit}>
                  <div className="space-y-5 px-6 py-5">
                    {/* Project Name */}
                    <div>
                      <label
                        htmlFor="project-name"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Project Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="project-name"
                        type="text"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="input-field mt-1.5 px-3"
                        placeholder="e.g., My Enterprise Application"
                      />
                    </div>

                    {/* Organization */}
                    <div>
                      <label
                        htmlFor="organization"
                        className="flex items-center text-sm font-medium text-gray-700"
                      >
                        Organization <span className="text-red-500 ml-0.5">*</span>
                        <InfoTooltip text="Select the organization this project belongs to. Every project must be associated with an organization for proper tracking and management." />
                      </label>
                      {orgsLoading ? (
                        <div className="mt-1.5 px-3 py-2 text-sm text-gray-400">Loading organizations...</div>
                      ) : organizations.length === 0 ? (
                        <div className="mt-1.5 px-3 py-2 text-sm text-amber-600 bg-amber-50 rounded-md border border-amber-200">
                          No organizations available. Please create an organization first.
                        </div>
                      ) : (
                        <select
                          id="organization"
                          required
                          value={organizationId}
                          onChange={(e) => setOrganizationId(e.target.value)}
                          className="input-field mt-1.5 px-3"
                        >
                          <option value="">-- Select Organization --</option>
                          {organizations.map((org) => (
                            <option key={org.id} value={org.id}>
                              {org.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    {/* Project UUID */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Project UUID
                      </label>
                      <p className="mt-0.5 text-xs text-gray-400">
                        A unique identifier will be automatically generated for this project.
                      </p>
                      <div className="mt-1.5 flex items-center gap-2">
                        <div className="flex-1 rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-mono text-gray-500 select-all">
                          {previewUUID}
                        </div>
                        <button
                          type="button"
                          onClick={handleCopyUUID}
                          className="rounded-md border border-gray-300 bg-white p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
                          title="Copy UUID"
                        >
                          <ClipboardDocumentIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Project Manager */}
                    <div>
                      <label
                        htmlFor="project-manager"
                        className="flex items-center text-sm font-medium text-gray-700"
                      >
                        Project Manager <span className="text-red-500 ml-0.5">*</span>
                        <InfoTooltip text="The Project Manager is the person in charge of this project and is responsible for adding and managing all the software components used within this project." />
                      </label>
                      <input
                        id="project-manager"
                        type="text"
                        required
                        value={projectManager}
                        onChange={(e) => setProjectManager(e.target.value)}
                        className="input-field mt-1.5 px-3"
                        placeholder="e.g., John Smith"
                      />
                    </div>

                    {/* Security Expert */}
                    <div>
                      <label
                        htmlFor="security-expert"
                        className="flex items-center text-sm font-medium text-gray-700"
                      >
                        Security Expert <span className="text-red-500 ml-0.5">*</span>
                        <InfoTooltip text="The Security Expert is the designated security professional who will receive all security vulnerability notifications and advisories for the components registered under this project." />
                      </label>
                      <input
                        id="security-expert"
                        type="text"
                        required
                        value={securityExpert}
                        onChange={(e) => setSecurityExpert(e.target.value)}
                        className="input-field mt-1.5 px-3"
                        placeholder="e.g., Jane Doe"
                      />
                    </div>

                    {/* Security Expert Email */}
                    <div>
                      <label
                        htmlFor="security-expert-email"
                        className="flex items-center text-sm font-medium text-gray-700"
                      >
                        Security Expert Email <span className="text-red-500 ml-0.5">*</span>
                        <InfoTooltip text="This corporate email address is used exclusively for delivering automated vulnerability alerts, security advisories, and remediation notifications related to the components within this project. Only business email addresses are accepted." />
                      </label>
                      <input
                        id="security-expert-email"
                        type="email"
                        required
                        value={securityExpertEmail}
                        onChange={(e) => setSecurityExpertEmail(e.target.value)}
                        className={`input-field mt-1.5 px-3 ${
                          securityExpertEmail && !isEmailValid
                            ? 'border-red-400 focus:ring-red-400'
                            : ''
                        }`}
                        placeholder="e.g., jane.doe@company.com"
                      />
                      {securityExpertEmail && !EMAIL_RE.test(securityExpertEmail) && (
                        <p className="mt-1 text-xs text-red-600">Please enter a valid email address.</p>
                      )}
                      {securityExpertEmail && EMAIL_RE.test(securityExpertEmail) && PERSONAL_DOMAINS.has(emailDomain) && (
                        <p className="mt-1 text-xs text-red-600">Personal email domains are not permitted. Please use a corporate email address.</p>
                      )}
                    </div>

                    {/* Description (optional) */}
                    <div>
                      <label
                        htmlFor="project-desc"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Description
                      </label>
                      <textarea
                        id="project-desc"
                        rows={3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="input-field mt-1.5 px-3 resize-none"
                        placeholder="Brief description of this project (optional)"
                      />
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-xl">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={!isValid || createProject.isPending}
                      className="btn-primary"
                    >
                      {createProject.isPending ? 'Creating…' : 'Create Project'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

// ── Project Card ─────────────────────────────────────────────────────────
function ProjectCard({
  project,
  onEdit,
  onDelete,
}: {
  project: Project;
  onEdit: (project: Project) => void;
  onDelete: (project: Project) => void;
}) {
  return (
    <div className="group relative rounded-xl border border-gray-200 bg-white p-5 shadow-sm hover:shadow-md hover:border-primary-200 transition-all duration-200">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 group-hover:bg-primary-100 transition-colors">
            <FolderIcon className="h-5 w-5 text-primary-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 group-hover:text-primary-700 transition-colors">
              {project.name}
            </h3>
            <p className="mt-0.5 text-xs font-mono text-gray-400">
              {project.id}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {/* Edit button */}
          <button
            type="button"
            onClick={() => onEdit(project)}
            className="rounded-md p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
            title="Edit project"
          >
            <PencilSquareIcon className="h-4 w-4" />
          </button>
          {/* Delete button */}
          <button
            type="button"
            onClick={() => onDelete(project)}
            className="rounded-md p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
            title="Delete project"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {project.description && (
        <p className="mt-3 text-sm text-gray-500 line-clamp-2">
          {project.description}
        </p>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3 border-t border-gray-100 pt-4">
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wide">
            Project Manager
          </p>
          <p className="mt-0.5 text-sm font-medium text-gray-700">
            {project.project_manager || '—'}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wide">
            Security Expert
          </p>
          <p className="mt-0.5 text-sm font-medium text-gray-700">
            {project.security_expert || '—'}
          </p>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between text-xs text-gray-400">
        <span>
          Created {new Date(project.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
        </span>
        {project.component_count > 0 && (
          <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
            {project.component_count} component{project.component_count !== 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Edit Project Modal ───────────────────────────────────────────────────
function EditProjectModal({
  open,
  onClose,
  project,
}: {
  open: boolean;
  onClose: () => void;
  project: Project;
}) {
  const [name, setName] = useState(project.name);
  const [organizationId, setOrganizationId] = useState(project.organization_id || '');
  const [projectManager, setProjectManager] = useState(project.project_manager);
  const [securityExpert, setSecurityExpert] = useState(project.security_expert);
  const [securityExpertEmail, setSecurityExpertEmail] = useState(project.security_expert_email ?? '');
  const [description, setDescription] = useState(project.description || '');
  const updateProject = useUpdateProject();

  // Fetch organizations for dropdown
  const { data: orgsData, isLoading: orgsLoading } = useOrganizations({ page_size: 100 });
  const organizations = orgsData?.items ?? [];

  // Sync form state when a different project is selected for editing
  useEffect(() => {
    setName(project.name);
    setOrganizationId(project.organization_id || '');
    setProjectManager(project.project_manager);
    setSecurityExpert(project.security_expert);
    setSecurityExpertEmail(project.security_expert_email ?? '');
    setDescription(project.description || '');
  }, [project]);

  const PERSONAL_DOMAINS_EDIT = new Set(['gmail.com','yahoo.com','hotmail.com','outlook.com','live.com','aol.com','icloud.com','me.com','mac.com','protonmail.com','proton.me','tutanota.com','gmx.com','gmx.net','mail.com','yandex.com','yandex.ru','zoho.com','qq.com','163.com','126.com','msn.com','rediffmail.com','inbox.com','fastmail.com']);
  const EMAIL_RE_EDIT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const editEmailDomain = securityExpertEmail.split('@')[1]?.toLowerCase() ?? '';
  const isEditEmailValid = EMAIL_RE_EDIT.test(securityExpertEmail) && !PERSONAL_DOMAINS_EDIT.has(editEmailDomain);

  const isValid =
    name.trim().length > 0 &&
    organizationId.trim().length > 0 &&
    projectManager.trim().length > 0 &&
    securityExpert.trim().length > 0 &&
    isEditEmailValid;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    try {
      await updateProject.mutateAsync({
        id: project.id,
        data: {
          name: name.trim(),
          organization_id: organizationId,
          project_manager: projectManager.trim(),
          security_expert: securityExpert.trim(),
          security_expert_email: securityExpertEmail.trim().toLowerCase(),
          description: description.trim(),
        },
      });
      toast.success('Project updated successfully!');
      onClose();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { data?: { detail?: string } } };
        toast.error(axiosErr.response?.data?.detail || 'Failed to update project');
      } else {
        toast.error('Failed to update project. Please try again.');
      }
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-lg transform rounded-xl bg-white shadow-2xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50">
                      <PencilSquareIcon className="h-5 w-5 text-amber-600" />
                    </div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Edit Project
                    </Dialog.Title>
                  </div>
                  <button
                    type="button"
                    className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit}>
                  <div className="space-y-5 px-6 py-5">
                    {/* Project Name */}
                    <div>
                      <label
                        htmlFor="edit-project-name"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Project Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="edit-project-name"
                        type="text"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="input-field mt-1.5 px-3"
                        placeholder="e.g., My Enterprise Application"
                      />
                    </div>

                    {/* Organization */}
                    <div>
                      <label
                        htmlFor="edit-organization"
                        className="flex items-center text-sm font-medium text-gray-700"
                      >
                        Organization <span className="text-red-500 ml-0.5">*</span>
                        <InfoTooltip text="Select the organization this project belongs to. Every project must be associated with an organization for proper tracking and management." />
                      </label>
                      {orgsLoading ? (
                        <div className="mt-1.5 px-3 py-2 text-sm text-gray-400">Loading organizations...</div>
                      ) : organizations.length === 0 ? (
                        <div className="mt-1.5 px-3 py-2 text-sm text-amber-600 bg-amber-50 rounded-md border border-amber-200">
                          No organizations available. Please create an organization first.
                        </div>
                      ) : (
                        <select
                          id="edit-organization"
                          required
                          value={organizationId}
                          onChange={(e) => setOrganizationId(e.target.value)}
                          className="input-field mt-1.5 px-3"
                        >
                          <option value="">-- Select Organization --</option>
                          {organizations.map((org) => (
                            <option key={org.id} value={org.id}>
                              {org.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    {/* Project UUID (read-only) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Project UUID
                      </label>
                      <div className="mt-1.5 rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-mono text-gray-500 select-all">
                        {project.id}
                      </div>
                    </div>

                    {/* Project Manager */}
                    <div>
                      <label
                        htmlFor="edit-project-manager"
                        className="flex items-center text-sm font-medium text-gray-700"
                      >
                        Project Manager <span className="text-red-500 ml-0.5">*</span>
                        <InfoTooltip text="The Project Manager is the person in charge of this project and is responsible for adding and managing all the software components used within this project." />
                      </label>
                      <input
                        id="edit-project-manager"
                        type="text"
                        required
                        value={projectManager}
                        onChange={(e) => setProjectManager(e.target.value)}
                        className="input-field mt-1.5 px-3"
                        placeholder="e.g., John Smith"
                      />
                    </div>

                    {/* Security Expert */}
                    <div>
                      <label
                        htmlFor="edit-security-expert"
                        className="flex items-center text-sm font-medium text-gray-700"
                      >
                        Security Expert <span className="text-red-500 ml-0.5">*</span>
                        <InfoTooltip text="The Security Expert is the designated security professional who will receive all security vulnerability notifications and advisories for the components registered under this project." />
                      </label>
                      <input
                        id="edit-security-expert"
                        type="text"
                        required
                        value={securityExpert}
                        onChange={(e) => setSecurityExpert(e.target.value)}
                        className="input-field mt-1.5 px-3"
                        placeholder="e.g., Jane Doe"
                      />
                    </div>

                    {/* Security Expert Email */}
                    <div>
                      <label
                        htmlFor="edit-security-expert-email"
                        className="flex items-center text-sm font-medium text-gray-700"
                      >
                        Security Expert Email <span className="text-red-500 ml-0.5">*</span>
                        <InfoTooltip text="This corporate email address is used exclusively for delivering automated vulnerability alerts, security advisories, and remediation notifications related to the components within this project. Only business email addresses are accepted." />
                      </label>
                      <input
                        id="edit-security-expert-email"
                        type="email"
                        required
                        value={securityExpertEmail}
                        onChange={(e) => setSecurityExpertEmail(e.target.value)}
                        className={`input-field mt-1.5 px-3 ${
                          securityExpertEmail && !isEditEmailValid
                            ? 'border-red-400 focus:ring-red-400'
                            : ''
                        }`}
                        placeholder="e.g., jane.doe@company.com"
                      />
                      {securityExpertEmail && !EMAIL_RE_EDIT.test(securityExpertEmail) && (
                        <p className="mt-1 text-xs text-red-600">Please enter a valid email address.</p>
                      )}
                      {securityExpertEmail && EMAIL_RE_EDIT.test(securityExpertEmail) && PERSONAL_DOMAINS_EDIT.has(editEmailDomain) && (
                        <p className="mt-1 text-xs text-red-600">Personal email domains are not permitted. Please use a corporate email address.</p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="edit-project-desc"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Description
                      </label>
                      <textarea
                        id="edit-project-desc"
                        rows={3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="input-field mt-1.5 px-3 resize-none"
                        placeholder="Brief description of this project (optional)"
                      />
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-xl">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={!isValid || updateProject.isPending}
                      className="btn-primary"
                    >
                      {updateProject.isPending ? 'Saving…' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

// ── Delete Confirmation Dialog ───────────────────────────────────────────
function DeleteConfirmDialog({
  open,
  onClose,
  project,
}: {
  open: boolean;
  onClose: () => void;
  project: Project;
}) {
  const deleteProject = useDeleteProject();

  const handleDelete = async () => {
    try {
      await deleteProject.mutateAsync(project.id);
      toast.success(`Project "${project.name}" deleted successfully.`);
      onClose();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { data?: { detail?: string } } };
        toast.error(axiosErr.response?.data?.detail || 'Failed to delete project');
      } else {
        toast.error('Failed to delete project. Please try again.');
      }
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-md transform rounded-xl bg-white shadow-2xl transition-all">
                <div className="px-6 pt-6 pb-4">
                  {/* Icon + Title */}
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <Dialog.Title className="text-lg font-semibold text-gray-900">
                        Delete Project
                      </Dialog.Title>
                      <p className="mt-2 text-sm text-gray-500">
                        Are you sure you want to delete{' '}
                        <span className="font-semibold text-gray-900">"{project.name}"</span>?
                      </p>
                    </div>
                  </div>

                  {/* Warning */}
                  <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4">
                    <div className="flex items-start gap-3">
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-red-700">
                        <p className="font-semibold">This action is permanent and cannot be undone.</p>
                        <p className="mt-1.5">
                          The following will be permanently deleted:
                        </p>
                        <ul className="mt-1.5 list-disc pl-5 space-y-1">
                          <li>The project and all its settings</li>
                          <li>
                            All components registered under this project
                            {project.component_count > 0 && (
                              <span className="font-semibold"> ({project.component_count} component{project.component_count !== 1 ? 's' : ''})</span>
                            )}
                          </li>
                          <li>All vulnerability scan results and matches for those components</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-xl">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={deleteProject.isPending}
                    onClick={handleDelete}
                    className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <TrashIcon className="h-4 w-4" />
                    {deleteProject.isPending ? 'Deleting…' : 'Delete Permanently'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────
export function ProjectsPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { data, isLoading } = useProjects({ search, page: 1, page_size: 100 });

  // Edit / Delete state
  const [editProject, setEditProject] = useState<Project | null>(null);
  const [deleteProject, setDeleteProject] = useState<Project | null>(null);

  const projects = data?.items ?? [];
  const isEmpty = !isLoading && projects.length === 0 && !search;

  return (
    <div>
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your software projects and track their security posture.
          </p>
        </div>
        {!isEmpty && (
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="btn-primary mt-4 gap-2 sm:mt-0"
          >
            <PlusIcon className="h-5 w-5" />
            New Project
          </button>
        )}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="mt-12 flex items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
        </div>
      )}

      {/* Empty State */}
      {isEmpty && <EmptyState onAddProject={() => setModalOpen(true)} />}

      {/* Search + Grid (when projects exist) */}
      {!isEmpty && !isLoading && (
        <>
          {/* Search bar */}
          <div className="mt-6">
            <input
              type="text"
              placeholder="Search projects…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field max-w-sm px-3"
            />
          </div>

          {/* Project grid */}
          {projects.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {projects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  onEdit={setEditProject}
                  onDelete={setDeleteProject}
                />
              ))}
            </div>
          ) : (
            <div className="mt-12 text-center">
              <p className="text-sm text-gray-500">
                No projects found matching "<span className="font-medium">{search}</span>"
              </p>
            </div>
          )}
        </>
      )}

      {/* Add Project Modal */}
      <AddProjectModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />

      {/* Edit Project Modal */}
      {editProject && (
        <EditProjectModal
          open={!!editProject}
          onClose={() => setEditProject(null)}
          project={editProject}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deleteProject && (
        <DeleteConfirmDialog
          open={!!deleteProject}
          onClose={() => setDeleteProject(null)}
          project={deleteProject}
        />
      )}
    </div>
  );
}
